import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import type { Project } from '@/types';
import styles from './ProjectCard.module.scss';

interface ProjectCardProps {
  project: Project;
  index?: number;
  delay?: number;
  large?: boolean;
}

const aspectRatios = [
  '4 / 5',   // Project 01
  '1 / 1',   // Project 02
  '3 / 4',   // Project 03
  '16 / 10', // Project 04
  '4 / 5',   // Project 05
  '1 / 1',   // Project 06
  '3 / 4',   // Project 07
  '16 / 10', // Project 08
];

const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  index = 0,
  delay = 0,
  large = false,
}) => {
  const currentRatio = aspectRatios[index % aspectRatios.length];

  return (
    <motion.div
      className={`${styles.card} ${large ? styles.large : ''}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.05 }}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      <Link
        to={`/work/${project.id}`}
        className={styles.inner}
        id={`project-card-${project.id}`}
      >
        {/* Top Media / Visual Area with organic aspect-ratio */}
        <div
          className={styles.visual}
          style={
            {
              '--accent': project.accentColor,
              '--aspect-ratio': currentRatio,
            } as React.CSSProperties
          }
        >
          {project.image ? (
            <img src={project.image} alt={project.title} className={styles.visualImage} />
          ) : (
            <div className={styles.visualBg}>
              <div className={styles.glow} />
              <div className={styles.grid} />
              <span className={styles.projectInitial}>
                {project.title.charAt(0)}
              </span>
            </div>
          )}
        </div>

        {/* Info Content Area */}
        <div className={styles.info}>
          <div className={styles.meta}>
            <span className={styles.category}>{project.category}</span>
            <span className={styles.year}>{project.year}</span>
          </div>

          <h3 className={styles.title}>{project.title}</h3>
          <p className={styles.description}>{project.description}</p>

          <div className={styles.footer}>
            <div className={styles.tags}>
              {project.tags.slice(0, 3).map((tag) => (
                <span key={tag} className={styles.tag}>
                  {tag}
                </span>
              ))}
            </div>
            <span className={styles.cta} style={{ color: project.accentColor }}>
              <span>View Project</span>
              <ArrowUpRight size={15} className={styles.ctaIcon} />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default ProjectCard;
